import React, { FC, ReactNode, useEffect, useState } from 'react';
import clsx from 'clsx';

import Close from '@/assets/icons/Close';
import Notification from '@/components/UI/Notification/Notification';
import * as Dialog from '@radix-ui/react-dialog';

import DialogFooter from './DialogFooter';

import styles from './Dialog.module.css';

type MwdDialogType = {
  title: ReactNode;
  showDialog: boolean;
  scrollEnabled?: boolean;
  headerSeparator?: boolean;
  hasError?: boolean;
  resetErrorState?: () => void;
  hasCloseInHeader?: boolean;
  handleOpenChange: (p: boolean) => void;
  children?: ReactNode;
  testId?: string;
  className?: string;
  paddingTopChildren?: boolean;
  showNotification?: boolean;
  notificationText?: string;
  notificationType?: 'info' | 'warning' | 'error';
};
const MwdDialog: FC<MwdDialogType> & { Footer: typeof DialogFooter } = ({
  title,
  showDialog,
  scrollEnabled,
  hasError,
  resetErrorState,
  handleOpenChange,
  headerSeparator = false,
  hasCloseInHeader = false,
  children,
  className = '',
  paddingTopChildren = true,
  testId,
  showNotification = false,
  notificationText,
  notificationType,
}) => {
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (hasError) {
      setShowError(true);
      timeoutId = setTimeout(() => {
        setShowError(false);
        resetErrorState?.();
      }, 1800);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [hasError, resetErrorState]);

  return (
    <Dialog.Root open={showDialog} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.dialogOverlayStyle} />
        <Dialog.Content
          aria-describedby={undefined}
          data-testid={testId}
          className={clsx(
            styles.dialogContentStyle,
            showError && styles.hasError,
            scrollEnabled && styles.scrollEnabled,
            className,
          )}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: '16px',
              paddingRight: '16px',
              paddingTop: '16px',
            }}
          >
            <Dialog.Title className={styles.dialogTitleStyle}>{title}</Dialog.Title>
            {hasCloseInHeader && (
              <div
                data-testid='dialog-close-button'
                className={styles.dialogCloseButtonStyle}
                onClick={() => handleOpenChange(false)}
              >
                <Close fill='var(--tractie-color-content-subtle)' aria-hidden='true' />
              </div>
            )}
          </div>
          {headerSeparator && <div className={styles.headerSeparatorStyle} />}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '16px',
              paddingTop: paddingTopChildren ? '16px' : '0px',
            }}
          >
            {showNotification && <Notification variant={notificationType}>{notificationText}</Notification>}
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
MwdDialog.Footer = DialogFooter;
export default MwdDialog;
